import Box from "@mui/material/Box";

import { error, misc, neutral, success } from "app/themes/colors";
import type { VoiceStatus } from "services/dialstack";

import { dndGlyphSx, recordGlyphSx } from "./dialstack-softphone-widget.styles";

// Until the phone's live presence feed is up, the directory is re-read this
// often while a colleague list is open; once it is, the phone pushes every
// change and the directory is only re-read for what Suno knows on top (a busy
// calendar, a scribe recording — ENG-2022), which moves far more slowly. The
// Rolodex's Team tab has no live feed and reads at the slower cadence.
export const PRESENCE_POLL_MS = 5000;
export const SUNO_STATUS_POLL_MS = 30000;

// How a colleague's status reads on a white row, shared by the softphone's
// transfer picker and the Rolodex's Team tab (ENG-2177). The dot colours hold
// 3:1 on white; the label beside the dot means hue never carries it alone. A
// busy calendar reads as Do Not Disturb with the reason in words; a scribe
// recording is the on-call orange with a record glyph (ENG-2022).
export type StatusLook = {
  label: string;
  color: string;
  glyph?: "dnd" | "record" | "ring";
};

export const STATUS_LOOKS: Record<VoiceStatus, StatusLook> = {
  available: { label: "Available", color: success[600] },
  on_call: { label: "On a call", color: misc.warningText },
  dnd: { label: "Do not disturb", color: error[600], glyph: "dnd" },
  with_patient: { label: "With a patient", color: error[600], glyph: "dnd" },
  in_meeting: { label: "In a meeting", color: error[600], glyph: "dnd" },
  recording: {
    label: "Recording a scribe",
    color: misc.warningText,
    glyph: "record"
  },
  // Reachable, but not at a desk: a ring rather than a full dot.
  mobile: { label: "Mobile only", color: success[600], glyph: "ring" },
  offline: { label: "Offline", color: neutral[700] }
};

// A status this build has never heard of shows no dot rather than throwing:
// the backend can grow the list before the frontend ships.
export const statusLook = (
  status: VoiceStatus | null | undefined
): StatusLook | undefined => (status ? STATUS_LOOKS[status] : undefined);

// The dot itself, meant to sit in the corner of an avatar (the parent is
// `position: relative`). Renders nothing for an unknown status.
export default function PresenceDot({
  status,
  testIdPrefix,
  size = 12
}: {
  status: VoiceStatus | null | undefined;
  // `${prefix}${status}` — specs read the status off the test id.
  testIdPrefix: string;
  size?: number;
}) {
  const look = statusLook(status);
  if (!look) return null;
  return (
    <Box
      aria-hidden
      data-testid={`${testIdPrefix}${status}`}
      sx={{
        position: "absolute",
        right: -2,
        bottom: -2,
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2px solid #FFFFFF",
        bgcolor: look.color,
        ...(look.glyph === "dnd" && dndGlyphSx),
        ...(look.glyph === "record" && recordGlyphSx),
        ...(look.glyph === "ring" && {
          bgcolor: "#FFFFFF",
          boxShadow: `inset 0 0 0 2px ${look.color}`
        })
      }}
    />
  );
}
